<template>
  <div
    class="HomeView">

    <div
      id="BannerGradient">
      <img
        id="BannerImage"
        src="@/assets/app-pillars-2.png"
        alt="app pillars"
      />
    </div>

    <div
      class="Content container-lg">

      <!-- HERO -->
      <div>
        <h1 class="font-normal">
          Domo Appstore
        </h1>
        <h3 class="font-normal mt-onehalf">
          Easily find new solutions and get the insights you need.
        </h3>
      </div>

      <!-- SEARCH BAR -->
      <form
        @submit.prevent="$router.push({
          name: 'search',
          query: searchText ? { q: searchText } : {},
        })">
        <BaseInput
          id="BannerSearchField"
          v-model.trim="searchText"
          placeholder="Search Apps"
          icon="search"
          :height="48"
          no-border
          show-clear-text
        />
      </form>

      <!-- FEATURED APPS -->
      <div
        class="FeaturedApps">
        <h3>Featured Apps</h3>
        <BaseCarousel
          class="mt-3"
          :slides="featuredSlides"
          show-radios
          show-arrows
          :gutter="12"
          :show-slides="[
            { size: 800, slides: 2 },
          ]"
          @click-slide="handleSlideClick">
        </BaseCarousel>
      </div>

      <!-- CATEGORIES & QUICKSTARTS -->
      <section
        class="AppGroup-and-Category">
        <div
          class="Categories">
          <h3
            class="Categories_header">
            Categories
          </h3>

          <BaseCarousel
            class="CategoryCarousel"
            :slides="categorySlides"
            :gutter="8"
            :peek="0.2"
            @click-slide="handleSlideClick">
          </BaseCarousel>

          <div
            class="CategoryCards">
            <CategoryCard
              class="CategoryCard"
              v-for="category in categories"
              :title="category.title"
              :overview="category.overview"
              :color="category.color"
              :key="category.title"
              :to="category.to"
            />
          </div>
        </div>

        <div
          class="AppGroup">
          <div
            class="AppGroup_header">
            <h3>Connect and visualize</h3>
            <BaseButton
              class="AppGroup_header_cta color-dark-blue"
              size="small"
              theme="ghost"
              :to="{ name: 'search', query: { q: 'quickstart' } }">
              VIEW ALL
            </BaseButton>
          </div>

          <BaseCarousel
            class="AppGroup_carousel"
            :slides="quickstartSlides"
            :gutter="8"
            :peek="0.15"
            @click-slide="handleSlideClick">
          </BaseCarousel>

          <div
            class="AppGroup_cards">
            <AppCard
              class="AppGroup_card"
              v-for="quickstart in featuredQuickstarts"
              :app="quickstart"
              :is-loading="loadingFeaturedData"
              :key="quickstart.title"
              @click="handleAppClick(quickstart)"
            />
          </div>
        </div>
      </section>

      <!-- CAPABILITIES & TRENDING -->
      <section
        class="AppGroup-and-Category row-reverse">
        <div
          class="Categories">
          <h3
            class="Categories_header">
            Capabilities
          </h3>

          <BaseCarousel
            class="CategoryCarousel"
            :slides="capabilitySlides"
            :gutter="8"
            :peek="0.2"
            @click-slide="handleSlideClick">
          </BaseCarousel>

          <div
            class="CategoryCards">
            <CategoryCard
              class="CategoryCard"
              v-for="capability in capabilities"
              :title="capability.title"
              :overview="capability.overview"
              :color="capability.color"
              :key="capability.title"
              :to="capability.to"
            />
          </div>
        </div>

        <div
          class="AppGroup">
          <div
            class="AppGroup_header">
            <h3>New and trending</h3>
            <BaseButton
              class="AppGroup_header_cta color-dark-blue"
              size="small"
              theme="ghost"
              :to="{ name: 'search' }">
              VIEW ALL
            </BaseButton>
          </div>

          <BaseCarousel
            class="AppGroup_carousel"
            :slides="trendingSlides"
            :gutter="8"
            :peek="0.15"
            @click-slide="handleSlideClick">
          </BaseCarousel>

          <div
            class="AppGroup_cards">
            <AppCard
              class="AppGroup_card"
              v-for="trending in featuredTrending"
              :app="trending"
              :is-loading="loadingFeaturedData"
              :key="trending.title"
              @click="handleAppClick(trending)"
            />
          </div>
        </div>
      </section>

      <!-- CONNECTORS -->
      <section
        v-if="featuredConnectors.length"
        class="mt-5"
        key="HV-FeaturedConnectors">
        <div
          class="AppGroup AppGroup-connectors">
          <div
            class="AppGroup_header">
            <h3>Popular ways to connect data</h3>
            <BaseButton
              class="AppGroup_header_cta color-dark-blue"
              size="small"
              theme="ghost"
              :to="{ name: 'search', query: { capability: 'Connector' } }">
              VIEW ALL
            </BaseButton>
          </div>

          <BaseCarousel
            class="AppGroup_carousel"
            :slides="connectorSlides"
            :gutter="8"
            :peek="0.15"
            @click-slide="handleSlideClick">
          </BaseCarousel>

          <div
            class="AppGroup_cards">
            <AppCard
              class="AppGroup_card"
              v-for="connector in featuredConnectors"
              :app="connector"
              :is-loading="loadingFeaturedData"
              :key="connector.title"
              @click="handleAppClick(connector)"
            />
          </div>
        </div>
      </section>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import BaseInput from '@/components/BaseInput.vue';
import BaseButton from '@/components/BaseButton.vue';
import BaseCarousel from '@/components/BaseCarousel.vue';
import AppCard from '@/components/AppCard.vue';
import CategoryCard from '@/components/CategoryCard.vue';
import { FEATURED } from '@/queries';

const EXCEPTIONS = [
  {
    appstoreID: 'awsdx',
    to: { name: 'aws-search' },
  },
];

const CATEGORIES = [
  {
    title:    'Marketing',
    overview: 'Track spend, increase influence, and improve ROI.',
    color:    'purple',
    to:       { name: 'search', query: { category: 'Marketing' } },
  },
  {
    title:    'Finance',
    overview: 'Grow the business with commerce integrations and analytics.',
    color:    'teal',
    to:       { name: 'search', query: { category: 'Finance' } },
  },
  {
    title:    'IT',
    overview: 'Import and visualize IT data from a variety of services.',
    color:    'orange',
    to:       { name: 'search', query: { category: 'IT' } },
  },
  {
    title:    'Sales',
    overview: 'Track your funnel and help convert leads.',
    color:    'green',
    to:       { name: 'search', query: { category: 'Sales' } },
  },
];

const CAPABILITIES = [
  {
    title:    'Data Science',
    overview: 'Discover new insights with these helpful tools.',
    color:    'purple',
    to:       { name: 'search', query: { capability: 'Data Science' } },
  },
  {
    title:    'Enterprise Apps',
    overview: 'Leverage data with custom visualizations and capabilities.',
    color:    'teal',
    to:       { name: 'search', query: { capability: 'Enterprise App' } },
  },
  {
    title:    'Writeback',
    overview: 'Update systems outside of Domo using these powerful connectors.',
    color:    'orange',
    to:       { name: 'search', query: { capability: 'Writeback' } },
  },
  {
    title:    'DataFlows',
    overview: 'Extract, transform, and load (ETL) existing data in new ways.',
    color:    'green',
    to:       { name: 'search', query: { capability: 'DataFlow' } },
  },
];

export default {
  name: 'HomeView',

  components: {
    BaseInput,
    BaseButton,
    BaseCarousel,
    AppCard,
    CategoryCard,
  },

  data() {
    return {
      loadingFeaturedData: true,

      categories: CATEGORIES,
      capabilities: CAPABILITIES,

      searchText: '',

      featuredAppsData: null,
      quickstartData: null,
      trendingData: null,
      connectorData: null,

      categorySlides: CATEGORIES.map((category) => ({
        component: CategoryCard,
        props: category,
      })),

      capabilitySlides: CAPABILITIES.map((capability) => ({
        component: CategoryCard,
        props: capability,
      })),
    };
  },

  computed: {
    featuredSlides() {
      const iterable = this.featuredAppsData ? this.featuredAppsData : [{}, {}, {}, {}];

      return iterable.map((app) => ({
        component: AppCard,
        props: {
          app,
          isLoading: this.loadingFeaturedData,
          noGutter: true,
          feature: true,
          img: app.background,
          to: this.generateAppTo(app),
        },
      }));
    },

    quickstartSlides() {
      const iterable = this.quickstartData ? this.quickstartData : [{}, {}, {}, {}, {}, {}];

      return iterable.map((app) => ({
        component: AppCard,
        props: {
          app,
          isLoading: this.loadingFeaturedData,
          noGutter: true,
          to: this.generateAppTo(app),
        },
      }));
    },

    trendingSlides() {
      const iterable = this.trendingData ? this.trendingData : [{}, {}, {}, {}, {}, {}];

      return iterable.map((app) => ({
        component: AppCard,
        props: {
          app,
          isLoading: this.loadingFeaturedData,
          noGutter: true,
          to: this.generateAppTo(app),
        },
      }));
    },

    connectorSlides() {
      const iterable = (this.connectorData && this.connectorData.length)
        ? this.connectorData
        : [{}, {}, {}, {}, {}, {}];

      return iterable.map((app) => ({
        component: AppCard,
        props: {
          app,
          isLoading: this.loadingFeaturedData,
          noGutter: true,
          to: this.generateAppTo(app),
        },
      }));
    },

    featuredQuickstarts() {
      return this.quickstartData ? this.quickstartData : [{}, {}, {}, {}, {}, {}];
    },

    featuredTrending() {
      return this.trendingData ? this.trendingData : [{}, {}, {}, {}, {}, {}];
    },

    featuredConnectors() {
      if (this.loadingFeaturedData) return [{}, {}, {}, {}, {}, {}];
      return (this.connectorData && this.connectorData.length)
        ? this.connectorData
        : [];
    },
  },

  async created() {
    try {
      const { data } = await axios.get(FEATURED);

      this.featuredAppsData = data[0].content.slice(0, 4);
      this.quickstartData = data[1].content.slice(0, 6);
      this.trendingData = data[2].content.slice(0, 6);
      this.connectorData = data[3].content.slice(0, 6);

      this.loadingFeaturedData = false;

      this.$root.remoteDataFetched('Featured Data', true, data);
    } catch (err) {
      this.loadingFeaturedData = false;

      this.$root.remoteDataFetched('Featured Data', false, err);
    }
  },

  methods: {
    handleSlideClick(slide) {
      if (slide.component.name === 'AppCard') return this.handleAppClick(slide.props.app);
      return this.$router.push(slide.props.to);
    },

    handleAppClick(app) {
      const exception = EXCEPTIONS.find((e) => e.appstoreID === app.appstoreID);

      this.$analytics.anchanchu.trackEvent('clicked app', { 'app name': app.title });

      if (exception) {
        return this.$router.push(exception.to);
      }

      this.$root.setOptimisticDetailData(app);
      return this.$router.push(this.generateAppTo(app));
    },

    generateAppTo(app) {
      return {
        name: 'app-detail',
        params: {
          appType: app.type || 'app',
          appName: app.friendlyID || 'null',
          tab: 'overview',
        },
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/_variables';

#BannerGradient {
  position: absolute;
  width: 100%;
  height: 300px;
  overflow: hidden;
  background: $light-blue;
  background: linear-gradient(5.7deg, rgba(242, 248, 252, 0), #C8E2F4);
}

#BannerImage {
  position: absolute;
  max-width: 300px;
  top: 30%;
  left: 30%;
}

.HomeView .Content {
  position: relative;
  z-index: 1;
  padding: 12px;
  padding-bottom: 48px;
}

#BannerSearchField {
  width: 100%;
  margin-top: 48px;
  box-shadow: 0 3px 6px rgba($black, .2);
}

.FeaturedApps {
  margin-top: 40px;
  overflow: hidden;
}

.AppGroup-and-Category {
  margin-top: 32px;
}

.Categories {
  flex: 0 1 290px;
}

.AppGroup {
  flex: 1 1 auto;
}

.Categories {
  min-width: 290px;
  margin-right: 20px;
}

.CategoryCards {
  display: none;
  padding-right: 32px;
  border-right: $border;
}

.CategoryCard {
  margin-top: 24px;
}

.CategoryCarousel {
  margin-top: 16px;
}

.AppGroup_carousel {
  margin-top: 8px;
}

.AppGroup_header {
  display: flex;
  margin-top: 32px;
  justify-content: space-between;
  align-items: flex-start;
}

.AppGroup_header_cta {
  margin-top: -8px;
}

.AppGroup_cards {
  display: none;
  margin-top: 12px;
  flex-wrap: wrap;
}

.AppGroup_card {
  flex: 1 1 100%;
  height: 230px;
  &:nth-child(n+3) {
    display: none;
  }
}

.AppGroup-connectors .AppGroup_card:nth-child(3),
.AppGroup-connectors .AppGroup_card:nth-child(4) {
  display: block !important;
}

// DESKTOP & LARGER

@media only screen and (min-width: 700px) {
  #BannerGradient {
    height: 400px;
  }

  #BannerImage {
    max-width: none;
    top: 20px;
    right: -32px;
    left: auto;
  }

  #BannerSearchField {
    width: 65%;
    max-width: 1000px;
  }

  .HomeView .Content {
    padding: 32px;
  }

  .AppGroup-and-Category {
    display: flex;
    margin-top: 40px;
  }

  .AppGroup-and-Category.row-reverse .Categories {
    margin-right: 0;
    margin-left: 20px;
    .Categories_header {
      padding-left: 32px;
    }
    .CategoryCards {
      padding-left: 32px;
      padding-right: 0;
      border-left: $border;
      border-right: none;
    }
  }

  .CategoryCarousel {
    display: none !important;
  }

  .CategoryCards {
    display: block !important;
  }

  .AppGroup_header {
    margin-top: 0;
    padding-left: 12px;
  }

  .AppGroup_carousel {
    display: none;
  }

  .AppGroup_cards {
    display: flex !important;
  }

  .AppGroup-connectors .AppGroup_card {
    flex: 1 1 40%;
  }
}

@media only screen and (min-width: 1075px) {
  .AppGroup_card {
    flex: 1 1 40%;
  }

  .AppGroup-connectors .AppGroup_card {
    flex: 1 1 30%;
  }

  .AppGroup-connectors .AppGroup_card:nth-child(5),
  .AppGroup-connectors .AppGroup_card:nth-child(6) {
    display: block !important;
  }

  .AppGroup_card:nth-child(3),
  .AppGroup_card:nth-child(4) {
    display: block !important;
  }
}

@media only screen and (min-width: 1420px) {
  .AppGroup_card {
    flex: 1 1 30%;
  }
  .AppGroup_card:nth-child(5),
  .AppGroup_card:nth-child(6) {
    display: block !important;
  }
}
</style>
